<i18n src="./resources/locales.json"></i18n>

<template>
  <div>
    <v-btn
      v-if="showDialpadIcon"
      :disabled="disabled"
      class="dialpad-fab"
      color="accent"
      fab
      @click="visible = !visible"
    >
      <v-icon>{{ 'mdi-dialpad' }}</v-icon>
    </v-btn>

    <v-btn v-if="showPhoneIcon" class="dialpad-fab" color="accent" fab @click="dial">
      <v-icon>{{ 'mdi-phone' }}</v-icon>
    </v-btn>

    <v-overlay class="dialpad-overlay" :value="visible" absolute :color="embedded ? 'transparent' : '#000e'">
      <div
        class="dialpad-container"
        :dark="$vuetify.theme.dark"
        :class="dialpadContainerStyle"
        @click="handleOverlayClick"
      >
        <div class="ecs-dialpad" @click.stop>
          <v-row v-if="showAutocomplete && !agentState.isBreak" no-gutters>
            <v-autocomplete
              v-model="businessProcessId"
              :dark="$vuetify.theme.dark"
              item-text="name"
              item-value="id"
              :label="$t('businessProcess')"
              :items="availableBusinessProcessesToDisplay"
              @change="focusInput"
            />
          </v-row>
          <v-row v-if="showAutocomplete && !agentState.isBreak" no-gutters>
            <v-autocomplete
              v-model="businessProcessDID"
              :dark="$vuetify.theme.dark"
              item-text="label"
              item-value="value"
              :label="$t('businessProcessDIDs')"
              :items="businessProcessDIDsToDisplay"
              @change="focusInput"
            />
          </v-row>
          <div v-if="agentState.isBreak && !hideBp" class="private-mode">
            <div class="private-mode-title">{{ $t('privateMode') }}</div>
            <div class="private-mode-message">{{ $t('privateModeMessage') }}</div>
          </div>
          <v-row no-gutters>
            <v-text-field
              ref="dialInput"
              v-model="value"
              class="dial-input"
              label="Phone Number"
              @keypress="handleKeypress"
              @keypress.enter="dial"
              @keydown.esc="close"
              @focus="pasteClipboard"
            >
              <v-btn
                v-if="!showOnlyDigits"
                slot="append"
                x-small
                class="ma-2 btn-text-icon"
                text
                icon
                color="orange darken-2"
                :class="{ 'hide-backspace': value === '' }"
                @click="deleteLast()"
              >
                <v-icon>mdi-backspace</v-icon>
              </v-btn>
              <v-btn
                v-if="showSendButton"
                slot="append"
                x-small
                class="ma-2 btn-text-icon"
                text
                icon
                color="orange darken-2"
                @click="dial()"
              >
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </v-text-field>
          </v-row>
          <v-row no-gutters>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(1)">1</v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(2)">2</v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(3)">3</v-btn>
          </v-row>
          <v-row no-gutters>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(4)">4</v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(5)">5</v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(6)">6</v-btn>
          </v-row>
          <v-row no-gutters>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(7)">7</v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(8)">8</v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(9)">9</v-btn>
          </v-row>
          <v-row no-gutters>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick('*')">
              *
            </v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick(0)">0</v-btn>
            <v-btn class="ma-2 dialpad-key" outlined fab dark small @click="handleDigitClick('#')">
              #
            </v-btn>
          </v-row>
        </div>
      </div>
    </v-overlay>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'EcsDialpad',

  props: {
    hideBp: {
      type: Boolean,
      default: false
    },
    hideFab: {
      type: Boolean,
      default: false
    },
    showOnlyDigits: {
      type: Boolean,
      default: false
    },
    showSendButton: {
      type: Boolean,
      default: false
    },
    embedded: {
      type: Boolean,
      default: false
    },
    show: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    centered: {
      type: Boolean,
      default: false
    },
    closeOnOverlayClick: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      businessProcessId: '',
      businessProcessDID: '',
      value: '',
      visible: false
    };
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', ['availableBusinessProcesses', 'supervisedBusinessProcesses']),

    ...mapGetters('REFERENCES', ['getBusinessProcessesDIDs']),

    ...mapGetters('SETTINGS', ['agentSettings', 'preferencesSettings']),

    ...mapGetters('AGENT', ['agentState']),

    dialpadContainerStyle() {
      return {
        'dialpad-container-centered': this.centered,
        embedded: this.embedded
      };
    },

    businessProcessesDIDs() {
      return this.getBusinessProcessesDIDs(this.businessProcessId);
    },

    availableBusinessProcessesToDisplay() {
      const supervisedBPs = (this.supervisedBusinessProcesses || []).map(bp => {
        return {
          id: bp.businessProcessId,
          name: bp.businessProcessName
        };
      });
      return [...this.availableBusinessProcesses, ...supervisedBPs];
    },

    businessProcessDIDsToDisplay() {
      return this.businessProcessesDIDs.map(bp => ({
        label: bp.name ? `${bp.did} (${bp.name})${bp.isDefault ? ' • ORG Default' : ''}` : bp.did,
        value: bp.did
      }));
    },

    defaultCallingLineIdentification() {
      // 1. Check for agent default DID
      let CLID = (this.agentSettings.defaultCallingLineIdentificationPerBP || []).find(
        config => config.businessProcessId === this.businessProcessId
      );

      // 2. Check if the default DID is available in the available business processes
      const availableBusinessProcesses = this.availableBusinessProcesses.map(bp => bp.id);
      if (!availableBusinessProcesses.includes(this.agentSettings.defaultOutgoingCallBusinessProcessId)) {
        return null;
      }

      // 3. If the agent does not have any selected default it will try with ORG default DID.
      if (!CLID || (CLID && !CLID.CLID)) {
        const orgDefault = (this.businessProcessesDIDs || []).find(config => config.isDefault);
        if (orgDefault) {
          CLID = { CLID: orgDefault.did, businessProcessId: this.businessProcessId };
        }
      }

      return CLID ? CLID.CLID : null;
    },

    showDialpadIcon() {
      return !this.hideFab && !this.visible && !this.showOnlyDigits;
    },

    showPhoneIcon() {
      return !this.hideFab && this.visible && !this.showOnlyDigits;
    },

    showAutocomplete() {
      return !this.showOnlyDigits && !this.hideBp;
    }
  },

  watch: {
    businessProcessId() {
      this.businessProcessDID = this.defaultCallingLineIdentification;
    },

    visible(value) {
      if (value) {
        if (!this.businessProcessId) {
          const fallbackBpId =
            this.availableBusinessProcesses &&
            this.availableBusinessProcesses[0] &&
            this.availableBusinessProcesses[0].id;
          this.businessProcessId = this.agentSettings.defaultOutgoingCallBusinessProcessId || fallbackBpId;
          this.businessProcessDID = this.defaultCallingLineIdentification;
        }
        setTimeout(() => {
          this.focusInput();
        }, 100);
        this.$root.$emit('close-side-panels');
      } else {
        // Default values on-close
        this.value = '';
        this.businessProcessId = '';
      }
    },

    show(value) {
      this.visible = value;
    },

    value() {
      this.$emit('change', this.value);
    }
  },

  mounted() {
    this.$root.$on('close-dialpad', this.close);
  },

  beforeDestroy() {
    this.$root.$off('close-dialpad', this.close);
  },

  methods: {
    close() {
      this.$emit('dialpad-closed');
      this.visible = false;
      this.value = '';
    },

    deleteLast() {
      this.value = this.value.slice(0, -1);
    },

    dial() {
      if (!this.value) return;

      this.$emit('dial', {
        businessProcessId: this.businessProcessId,
        businessProcessDID: this.businessProcessDID,
        phoneNumber: this.value
      });

      this.close();
    },

    pasteClipboard() {
      navigator.clipboard
        .readText()
        .then(clipboardText => {
          if (clipboardText) {
            if (this.value) {
              // Prevents replacing a pre-populated number when calling from the directory section.
            } else {
              this.value = (clipboardText || '').replaceAll(/[^+0-9]/g, '');
            }
          }
        })
        .catch(() => {});
    },

    focusInput() {
      this.visible = true;
      const input = this.$el.querySelector('.dial-input input');
      if (input) {
        input.focus();
      }
    },

    /**
     * Plays a DTMF tone for the given key (0-9, *, #) as user feedback.
     * This function does not send the DTMF tone to the call.
     * @param key
     */
    playTone(key) {
      if (this.preferencesSettings.muteDTMF) {
        return;
      }

      const frequencies = {
        '1': [697, 1209],
        '2': [697, 1336],
        '3': [697, 1477],
        '4': [770, 1209],
        '5': [770, 1336],
        '6': [770, 1477],
        '7': [852, 1209],
        '8': [852, 1336],
        '9': [852, 1477],
        '0': [941, 1336],
        '*': [941, 1209],
        '#': [941, 1477]
      };

      if (!frequencies[key]) {
        return;
      }

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0.3; // Volume 0.0 -> min, 1.0 -> max
      gainNode.connect(audioContext.destination);

      const [lowFreq, highFreq] = frequencies[key];

      const createOscillator = freq => {
        const osc = audioContext.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gainNode);
        return osc;
      };

      const lowOsc = createOscillator(lowFreq);
      const highOsc = createOscillator(highFreq);

      lowOsc.start();
      highOsc.start();

      setTimeout(() => {
        lowOsc.stop();
        highOsc.stop();
        audioContext.close();
      }, 200);
    },

    handleDigitClick(digit) {
      this.focusInput();
      this.value += digit;
      this.playTone(digit + '');
      this.$emit('digit-pressed', digit);
    },

    handleKeypress(event) {
      const { key } = event;
      this.playTone(key + '');
      this.$emit('digit-pressed', key);
    },

    setBusinessProcessId(businessProcessId) {
      this.businessProcessId = businessProcessId;
    },

    setValue(value) {
      this.value = value;
    },

    handleOverlayClick() {
      if (this.closeOnOverlayClick) {
        this.close();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.private-mode {
  max-width: 168px;
  margin-bottom: 4px;
}
.private-mode-title {
  font-size: 14px;
  padding-bottom: 4px;
}
.private-mode-message {
  font-size: 12px;
  opacity: 0.75;
}
.ecs-dialpad {
  z-index: 10;
  padding: 8px;
  border-radius: 8px;
  width: fit-content;
  position: absolute;
  bottom: 130px;
  right: 16px;
}

.dialpad-fab {
  position: fixed;
  right: 12px;
  bottom: 60px;
  z-index: 10;
}
.dialpad-key {
  font-size: 16px;
}

.dialpad-container {
  z-index: 5;

  &:not([dark]) {
    &.embedded .dialpad-key {
      border-color: #dcdcdc;
      color: #777;
    }

    .dialpad-key {
      border-color: #dcdcdc;
      color: #dcdcdc;
    }
  }
}

.dialpad-container-centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialpad-container-centered .ecs-dialpad {
  position: unset;
}
</style>

<style lang="scss">
.ecs-dialpad .v-input {
  width: 180px;
}

.hide-backspace {
  display: none;
}

.dialpad-overlay .v-overlay__content,
.dialpad-container {
  height: 100%;
  width: 100%;
}

.dialpad-container.embedded:not([dark]) {
  .dial-input.v-input input {
    color: #444;
    caret-color: auto;
  }

  div:not(.error--text) {
    .v-label {
      color: #999 !important;
    }

    &.v-input__slot {
      &:before,
      &:after {
        border-color: #999 !important;
      }
    }
  }
}
</style>
